/**
 * AdminWorkflowEngineTab Component
 *
 * Admin dashboard for distributed workflow engine monitoring and management.
 * Shows engine instances, execution metrics, workflow affinity, and recent claims.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  Server,
  Activity,
  Target,
  Clock,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Zap,
  Trash2,
  Pencil,
  X,
  Timer,
  TrendingUp,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Info,
} from 'lucide-react';
import {
  engineApi,
  EngineInstance,
  ExecutionClaim,
  EngineMetrics,
  EngineStats,
  WorkflowWithAffinity,
  WorkflowAffinity,
} from '@/services/api/engineApi';
import * as errorUtils from '@/utils/errors';

// --- Helpers ---

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 1000) return 'just now';
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

function statusColor(status: string): string {
  switch (status) {
    case 'online': return 'bg-green-500/10 text-green-600 border-green-500/20';
    case 'offline': return 'bg-red-500/10 text-red-600 border-red-500/20';
    case 'draining': return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
    case 'completed': return 'bg-green-500/10 text-green-600';
    case 'failed': return 'bg-red-500/10 text-red-600';
    case 'claimed': case 'executing': return 'bg-blue-500/10 text-blue-600';
    case 'pending': return 'bg-gray-500/10 text-gray-600';
    default: return 'bg-gray-500/10 text-gray-600';
  }
}

// --- Sub-components ---

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${statusColor(status)}`}>
    {status}
  </span>
);

const StatCard: React.FC<{ label: string; value: number | string; icon: React.ReactNode; color?: string; info?: string }> = ({ label, value, icon, color = 'text-foreground', info }) => (
  <div className="bg-card border border-border rounded-lg p-4 flex items-center gap-3 relative group">
    <div className={`p-2 rounded-md bg-muted ${color}`}>{icon}</div>
    <div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground flex items-center gap-1">
        {label}
        {info && (
          <span className="relative inline-flex">
            <Info className="h-3 w-3 text-muted-foreground/50 cursor-help" />
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 px-2 py-1.5 text-[11px] leading-tight bg-popover text-popover-foreground border border-border rounded-md shadow-md opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50">
              {info}
            </span>
          </span>
        )}
      </div>
    </div>
  </div>
);

// --- Workflow Config Dialog (Affinity + Environments) ---

const WorkflowConfigDialog: React.FC<{
  workflow: WorkflowWithAffinity;
  instances: EngineInstance[];
  allEnvironments: string[];
  onSaveAffinity: (workflowId: string, affinity: WorkflowAffinity) => void;
  onClearAffinity: (workflowId: string) => void;
  onSaveEnvironments: (workflowId: string, envs: string[]) => void;
  onClose: () => void;
}> = ({ workflow, instances, allEnvironments, onSaveAffinity, onClearAffinity, onSaveEnvironments, onClose }) => {
  const [affinity, setAffinity] = useState<WorkflowAffinity>(workflow.affinity || {});
  const [selectedEnvs, setSelectedEnvs] = useState<Set<string>>(new Set(workflow.runEnvironments || ['production']));
  const [customEnv, setCustomEnv] = useState('');

  const onlineInstances = instances.filter(i => i.status === 'online');

  const toggleEnv = (env: string) => {
    setSelectedEnvs(prev => {
      const next = new Set(prev);
      if (next.has(env)) next.delete(env);
      else next.add(env);
      return next;
    });
  };

  const addCustomEnv = () => {
    const env = customEnv.trim();
    if (env && !selectedEnvs.has(env)) {
      setSelectedEnvs(prev => new Set(prev).add(env));
    }
    setCustomEnv('');
  };

  const handleSave = () => {
    onSaveAffinity(workflow._id, affinity);
    const envs = Array.from(selectedEnvs);
    if (envs.length > 0) {
      onSaveEnvironments(workflow._id, envs);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Configure: {workflow.name}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-4">
          {/* Run Environments */}
          <div>
            <label className="text-sm font-medium">Run Environments</label>
            <div className="mt-1 space-y-1.5 max-h-32 overflow-y-auto border border-input rounded-md p-2">
              {Array.from(new Set([...allEnvironments, ...selectedEnvs])).sort().map(env => (
                <label key={env} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5">
                  <input
                    type="checkbox"
                    checked={selectedEnvs.has(env)}
                    onChange={() => toggleEnv(env)}
                    className="rounded border-input"
                  />
                  {env}
                </label>
              ))}
              {allEnvironments.length === 0 && selectedEnvs.size === 0 && (
                <p className="text-xs text-muted-foreground">No environments found. Add one below.</p>
              )}
            </div>
            <div className="flex gap-1 mt-2">
              <input
                className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-xs"
                value={customEnv}
                onChange={e => setCustomEnv(e.target.value)}
                placeholder="Add custom environment"
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomEnv())}
              />
              <button
                onClick={addCustomEnv}
                disabled={!customEnv.trim()}
                className="px-2 py-1 text-xs rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50"
              >
                Add
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Select which environments this workflow runs on.</p>
          </div>

          <div className="border-t border-border" />

          {/* Affinity fields */}
          <div>
            <label className="text-sm font-medium">Preferred Instance</label>
            <select
              className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={affinity.preferredInstance || ''}
              onChange={e => setAffinity({ ...affinity, preferredInstance: e.target.value || undefined })}
            >
              <option value="">None</option>
              {onlineInstances.map(i => <option key={i.instanceId} value={i.instanceId}>{i.name || i.instanceId} ({i.metadata.hostname})</option>)}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium">Required Instance</label>
            <select
              className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={affinity.requiredInstance || ''}
              onChange={e => setAffinity({ ...affinity, requiredInstance: e.target.value || undefined })}
            >
              <option value="">None (any instance)</option>
              {onlineInstances.map(i => <option key={i.instanceId} value={i.instanceId}>{i.name || i.instanceId} ({i.metadata.hostname})</option>)}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium">Preferred Cluster</label>
            <input
              className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={affinity.preferredCluster || ''}
              onChange={e => setAffinity({ ...affinity, preferredCluster: e.target.value || undefined })}
              placeholder="e.g. production"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Required Cluster</label>
            <input
              className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={affinity.requiredCluster || ''}
              onChange={e => setAffinity({ ...affinity, requiredCluster: e.target.value || undefined })}
              placeholder="e.g. edge-site-1"
            />
          </div>
        </div>

        <div className="flex justify-between mt-6">
          <Button variant="destructive" size="sm" onClick={() => { onClearAffinity(workflow._id); onClose(); }}>
            <Trash2 className="h-3 w-3 mr-1" /> Clear Affinity
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={handleSave}>Save</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Main Component ---

export const AdminWorkflowEngineTab: React.FC = () => {
  const [instances, setInstances] = useState<EngineInstance[]>([]);
  const [currentInstance, setCurrentInstance] = useState<string>('');
  const [metrics, setMetrics] = useState<EngineMetrics | null>(null);
  const [stats, setStats] = useState<EngineStats | null>(null);
  const [claims, setClaims] = useState<ExecutionClaim[]>([]);
  const [workflows, setWorkflows] = useState<WorkflowWithAffinity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingWorkflow, setEditingWorkflow] = useState<WorkflowWithAffinity | null>(null);
  const [claimFilter, setClaimFilter] = useState<{ status?: string; instanceId?: string }>({});
  const [workflowFilter, setWorkflowFilter] = useState<{ owner?: string; status?: string; environment?: string }>({});
  const [errorsExpanded, setErrorsExpanded] = useState(false);

  const instancesTimer = useRef<ReturnType<typeof setInterval>>();
  const metricsTimer = useRef<ReturnType<typeof setInterval>>();
  const claimsTimer = useRef<ReturnType<typeof setInterval>>();

  // Fetch functions
  const fetchInstances = useCallback(async () => {
    try {
      const data = await engineApi.getInstances();
      setInstances(data.instances);
      setCurrentInstance(data.currentInstance);
    } catch {}
  }, []);

  const fetchMetrics = useCallback(async () => {
    try {
      const data = await engineApi.getMetrics();
      setMetrics(data);
    } catch {}
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const data = await engineApi.getStats();
      setStats(data);
    } catch {}
  }, []);

  const fetchClaims = useCallback(async () => {
    try {
      const data = await engineApi.getClaims({ ...claimFilter, limit: 50 });
      setClaims(data.claims);
    } catch {}
  }, [claimFilter]);

  const fetchWorkflows = useCallback(async () => {
    try {
      const data = await engineApi.getWorkflowsWithAffinity();
      setWorkflows(data.workflows);
    } catch {}
  }, []);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await Promise.all([fetchInstances(), fetchMetrics(), fetchStats(), fetchClaims(), fetchWorkflows()]);
    } catch (err: unknown) {
      setError(errorUtils.getErrorMessageOrFallback(err, 'Failed to load engine data'));
    } finally {
      setIsLoading(false);
    }
  }, [fetchInstances, fetchMetrics, fetchStats, fetchClaims, fetchWorkflows]);

  // Initial load + auto-refresh
  useEffect(() => {
    fetchAll();
    instancesTimer.current = setInterval(fetchInstances, 10000);
    metricsTimer.current = setInterval(() => { fetchMetrics(); fetchStats(); }, 30000);
    claimsTimer.current = setInterval(fetchClaims, 15000);
    return () => {
      clearInterval(instancesTimer.current);
      clearInterval(metricsTimer.current);
      clearInterval(claimsTimer.current);
    };
  }, [fetchAll, fetchInstances, fetchMetrics, fetchClaims]);

  // Re-fetch claims when filter changes
  useEffect(() => { fetchClaims(); }, [fetchClaims]);

  // Affinity handlers
  const handleSaveAffinity = useCallback(async (workflowId: string, affinity: WorkflowAffinity) => {
    try {
      await engineApi.updateWorkflowAffinity(workflowId, affinity);
      fetchWorkflows();
    } catch {}
  }, [fetchWorkflows]);

  const handleClearAffinity = useCallback(async (workflowId: string) => {
    try {
      await engineApi.clearWorkflowAffinity(workflowId);
      fetchWorkflows();
    } catch {}
  }, [fetchWorkflows]);

  const handleSaveEnvironments = useCallback(async (workflowId: string, runEnvironments: string[]) => {
    try {
      await engineApi.updateWorkflowEnvironments(workflowId, runEnvironments);
      fetchWorkflows();
    } catch {}
  }, [fetchWorkflows]);

  const handleToggleStatus = useCallback(async (workflowId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    if (!window.confirm(`Change workflow status to "${newStatus}"?`)) return;
    try {
      await engineApi.toggleWorkflowStatus(workflowId);
      fetchWorkflows();
    } catch {}
  }, [fetchWorkflows]);

  // Workflow filter options (derived from loaded data)
  const ownerOptions = Array.from(new Set(workflows.map(w => w.ownerName || w.owner_type))).sort();
  const statusOptions = Array.from(new Set(workflows.map(w => w.status))).sort();
  const envOptions = Array.from(new Set(workflows.flatMap(w => w.runEnvironments || ['production']))).sort();

  const filteredWorkflows = workflows.filter(wf => {
    if (workflowFilter.owner && (wf.ownerName || wf.owner_type) !== workflowFilter.owner) return false;
    if (workflowFilter.status && wf.status !== workflowFilter.status) return false;
    if (workflowFilter.environment && !(wf.runEnvironments || ['production']).includes(workflowFilter.environment)) return false;
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" size="sm" onClick={fetchAll}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Server className="h-5 w-5" /> Workflow Engine
        </h2>
        <Button variant="outline" size="sm" onClick={fetchAll}>
          <RefreshCw className="h-3 w-3 mr-1" /> Refresh
        </Button>
      </div>

      {/* Section 1: Engine Instances */}
      <section>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Engine Instances</h3>
        {instances.length === 0 ? (
          <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4">No engine instances registered.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {instances.map(inst => (
              <div
                key={inst.instanceId}
                className={`bg-card border rounded-lg p-4 space-y-2 ${
                  inst.instanceId === currentInstance ? 'border-primary ring-1 ring-primary/20' : 'border-border'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex flex-col min-w-0">
                    <span className="text-base font-semibold truncate">
                      {inst.name || inst.instanceId}
                    </span>
                    {inst.name && (
                      <span className="font-mono text-xs text-muted-foreground truncate">
                        {inst.instanceId}
                      </span>
                    )}
                  </div>
                  <StatusBadge status={inst.status} />
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>Cluster: <span className="text-foreground">{inst.clusterId}</span></div>
                  <div>Host: <span className="text-foreground">{inst.metadata.hostname}</span></div>
                  <div>Platform: <span className="text-foreground">{inst.metadata.platform}</span></div>
                  <div>Version: <span className="text-foreground">{inst.version}</span></div>
                  <div>Heartbeat: <span className="text-foreground">{relativeTime(inst.lastHeartbeat)}</span></div>
                  <div>Started: <span className="text-foreground">{relativeTime(inst.startedAt)}</span></div>
                </div>
                {Object.keys(inst.labels).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(inst.labels).map(([k, v]) => (
                      <span key={k} className="inline-flex items-center px-1.5 py-0.5 text-[10px] bg-muted rounded">{k}={v}</span>
                    ))}
                  </div>
                )}
                {inst.resources.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {inst.resources.map(r => (
                      <span key={r} className="inline-flex items-center px-1.5 py-0.5 text-[10px] bg-blue-500/10 text-blue-600 rounded">{r}</span>
                    ))}
                  </div>
                )}
                {inst.instanceId === currentInstance && (
                  <div className="text-[10px] text-primary font-medium">← This instance</div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Section 2: Execution Metrics */}
      {metrics && (
        <section>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Execution Metrics (Last Hour)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard label="Total Claims" value={metrics.totalClaims} icon={<Activity className="h-4 w-4" />} info="Total execution claims created in the last hour across all instances" />
            <StatCard label="Completed" value={metrics.completed} icon={<CheckCircle2 className="h-4 w-4" />} color="text-green-600" info="Claims that finished successfully in the last hour" />
            <StatCard label="Failed" value={metrics.failed} icon={<XCircle className="h-4 w-4" />} color="text-red-600" info="Claims that encountered errors in the last hour" />
            <StatCard label="Active" value={metrics.active} icon={<Zap className="h-4 w-4" />} color="text-blue-600" info="Claims currently being claimed or executed right now" />
          </div>

          {/* Trigger type breakdown */}
          {Object.keys(metrics.byTriggerType).length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs font-medium text-muted-foreground mb-2">By Trigger Type</h4>
              <div className="flex flex-wrap gap-2">
                {Object.entries(metrics.byTriggerType).map(([type, count]) => (
                  <div key={type} className="bg-muted rounded-md px-3 py-1.5 text-sm">
                    <span className="text-muted-foreground">{type}:</span> <span className="font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Instance distribution */}
          {metrics.instanceDistribution.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-muted-foreground mb-2">Claims per Instance</h4>
              <div className="space-y-1.5">
                {metrics.instanceDistribution.map(({ instanceId, claims: count }) => {
                  const maxClaims = Math.max(...metrics.instanceDistribution.map(d => d.claims));
                  const pct = maxClaims > 0 ? (count / maxClaims) * 100 : 0;
                  return (
                    <div key={instanceId} className="flex items-center gap-3 text-sm">
                      <span className="font-mono text-xs w-40 truncate">{instanceId}</span>
                      <div className="flex-1 bg-muted rounded-full h-2">
                        <div className="bg-primary rounded-full h-2" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-medium w-8 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Section 3: Execution Statistics */}
      {stats && (
        <section>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Execution Statistics</h3>

          {/* Executions per minute sparkline */}
          <div className="bg-card border border-border rounded-lg p-4 mb-4">
            <h4 className="text-xs font-medium text-muted-foreground mb-2">Executions per Minute (Last 15 min)</h4>
            <div className="flex items-end gap-1 h-16">
              {stats.executionsPerMinute.map((point, i) => {
                const max = Math.max(...stats.executionsPerMinute.map(p => p.count), 1);
                const heightPct = (point.count / max) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${point.minute}: ${point.count}`}>
                    <div className="w-full bg-primary/80 rounded-t-sm transition-all" style={{ height: `${Math.max(heightPct, 2)}%` }} />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-muted-foreground">15m ago</span>
              <span className="text-[10px] text-muted-foreground">now</span>
            </div>
          </div>

          {/* Performance stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard
              label="Avg Duration"
              value={formatDuration(stats.avgDurationMs)}
              icon={<Timer className="h-4 w-4" />}
              info="Average execution time for completed workflows in the last hour"
            />
            <StatCard
              label="Success Rate"
              value={`${stats.successRate}%`}
              icon={<TrendingUp className="h-4 w-4" />}
              color={stats.successRate >= 95 ? 'text-green-600' : stats.successRate >= 80 ? 'text-yellow-600' : 'text-red-600'}
              info="Percentage of executions that completed successfully in the last hour"
            />
            <StatCard
              label="Active Now"
              value={stats.activeNow}
              icon={<Zap className="h-4 w-4" />}
              color="text-blue-600"
              info="Workflow executions currently running or paused (started in the last hour)"
            />
            <StatCard
              label="Errors (1h)"
              value={stats.failedLastHour}
              icon={<XCircle className="h-4 w-4" />}
              color={stats.failedLastHour > 0 ? 'text-red-600' : 'text-muted-foreground'}
              info="Total failed workflow executions in the last hour. Expand errors below for details"
            />
          </div>

          {/* Slowest workflows */}
          {stats.slowestWorkflows.length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs font-medium text-muted-foreground mb-2">Slowest Workflows (Last Hour)</h4>
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Workflow</th>
                      <th className="text-right px-4 py-2 font-medium">Avg Duration</th>
                      <th className="text-right px-4 py-2 font-medium">Max Duration</th>
                      <th className="text-right px-4 py-2 font-medium">Executions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {stats.slowestWorkflows.map(w => (
                      <tr key={w.workflowId} className="hover:bg-muted/30">
                        <td className="px-4 py-2">{w.workflowName}</td>
                        <td className="px-4 py-2 text-right font-mono text-xs">{formatDuration(w.avgDurationMs)}</td>
                        <td className="px-4 py-2 text-right font-mono text-xs">{formatDuration(w.maxDurationMs)}</td>
                        <td className="px-4 py-2 text-right">{w.executions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Error breakdown + details */}
          {stats.errorsByCode.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-medium text-muted-foreground">Errors by Type</h4>
                {stats.recentErrors && stats.recentErrors.length > 0 && (
                  <button
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                    onClick={() => setErrorsExpanded(!errorsExpanded)}
                  >
                    {errorsExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    {errorsExpanded ? 'Hide details' : 'View details'}
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {stats.errorsByCode.map(e => (
                  <div key={e.code} className="bg-red-500/10 text-red-600 rounded-md px-3 py-1.5 text-sm">
                    <span className="font-mono">{e.code}:</span> <span className="font-medium">{e.count}</span>
                  </div>
                ))}
              </div>

              {/* Expanded error details table */}
              {errorsExpanded && stats.recentErrors && stats.recentErrors.length > 0 && (
                <div className="bg-card border border-border rounded-lg overflow-hidden mt-3">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium">Time</th>
                        <th className="text-left px-4 py-2 font-medium">Workflow</th>
                        <th className="text-left px-4 py-2 font-medium">Error Code</th>
                        <th className="text-left px-4 py-2 font-medium">Message</th>
                        <th className="text-left px-4 py-2 font-medium">Node</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {stats.recentErrors.map((err, i) => (
                        <tr key={i} className="hover:bg-muted/30">
                          <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">{relativeTime(err.startedAt)}</td>
                          <td className="px-4 py-2">{err.workflowName}</td>
                          <td className="px-4 py-2 font-mono text-xs text-red-600">{err.errorCode}</td>
                          <td className="px-4 py-2 text-xs max-w-[300px] truncate" title={err.errorMessage}>{err.errorMessage}</td>
                          <td className="px-4 py-2 font-mono text-[10px] text-muted-foreground">{err.nodeId || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Section 4: Workflow Configuration */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Workflow Configuration</h3>
          <div className="flex gap-2">
            <select
              className="rounded-md border border-input bg-background px-2 py-1 text-xs"
              value={workflowFilter.owner || ''}
              onChange={e => setWorkflowFilter(f => ({ ...f, owner: e.target.value || undefined }))}
            >
              <option value="">All owners</option>
              {ownerOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <select
              className="rounded-md border border-input bg-background px-2 py-1 text-xs"
              value={workflowFilter.status || ''}
              onChange={e => setWorkflowFilter(f => ({ ...f, status: e.target.value || undefined }))}
            >
              <option value="">All statuses</option>
              {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              className="rounded-md border border-input bg-background px-2 py-1 text-xs"
              value={workflowFilter.environment || ''}
              onChange={e => setWorkflowFilter(f => ({ ...f, environment: e.target.value || undefined }))}
            >
              <option value="">All environments</option>
              {envOptions.map(env => <option key={env} value={env}>{env}</option>)}
            </select>
          </div>
        </div>
        {filteredWorkflows.length === 0 ? (
          <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4">
            {workflows.length === 0 ? 'No workflows found.' : 'No workflows match the selected filters.'}
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Workflow</th>
                    <th className="text-left px-4 py-2 font-medium">Owner</th>
                    <th className="text-left px-4 py-2 font-medium">Status</th>
                    <th className="text-left px-4 py-2 font-medium">Environments</th>
                    <th className="text-left px-4 py-2 font-medium">Preferred</th>
                    <th className="text-left px-4 py-2 font-medium">Required</th>
                    <th className="text-right px-4 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredWorkflows.map(wf => (
                    <tr key={wf._id} className="hover:bg-muted/30">
                      <td className="px-4 py-2 font-medium">{wf.name}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{wf.ownerName || wf.owner_type}</td>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => handleToggleStatus(wf._id, wf.status)}
                          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border cursor-pointer transition-colors ${
                            wf.status === 'published'
                              ? 'bg-green-500/10 text-green-600 border-green-500/20 hover:bg-green-500/20'
                              : 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20 hover:bg-yellow-500/20'
                          }`}
                          title={`Click to ${wf.status === 'published' ? 'unpublish' : 'publish'}`}
                        >
                          {wf.status}
                        </button>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-1">
                          {(wf.runEnvironments || ['production']).map(env => (
                            <span key={env} className="inline-flex items-center px-1.5 py-0.5 text-[10px] bg-blue-500/10 text-blue-600 rounded">{env}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">
                        {wf.affinity?.preferredInstance || wf.affinity?.preferredCluster || '—'}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">
                        {wf.affinity?.requiredInstance || wf.affinity?.requiredCluster || '—'}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => setEditingWorkflow(wf)}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Section 4: Recent Claims */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Recent Claims</h3>
          <div className="flex gap-2">
            <select
              className="rounded-md border border-input bg-background px-2 py-1 text-xs"
              value={claimFilter.status || ''}
              onChange={e => setClaimFilter(f => ({ ...f, status: e.target.value || undefined }))}
            >
              <option value="">All statuses</option>
              <option value="claimed">Claimed</option>
              <option value="executing">Executing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
            <select
              className="rounded-md border border-input bg-background px-2 py-1 text-xs"
              value={claimFilter.instanceId || ''}
              onChange={e => setClaimFilter(f => ({ ...f, instanceId: e.target.value || undefined }))}
            >
              <option value="">All instances</option>
              {instances.map(i => <option key={i.instanceId} value={i.instanceId}>{i.name || i.instanceId}</option>)}
            </select>
          </div>
        </div>

        {claims.length === 0 ? (
          <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4">No execution claims found.</div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Time</th>
                    <th className="text-left px-4 py-2 font-medium">Trigger Type</th>
                    <th className="text-left px-4 py-2 font-medium">Claimed By</th>
                    <th className="text-left px-4 py-2 font-medium">Status</th>
                    <th className="text-left px-4 py-2 font-medium">Trigger ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {claims.map(claim => (
                    <tr key={claim._id} className="hover:bg-muted/30">
                      <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">{relativeTime(claim.createdAt)}</td>
                      <td className="px-4 py-2">
                        <span className="inline-flex items-center px-1.5 py-0.5 text-xs bg-muted rounded">{claim.triggerType}</span>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">{claim.claimedBy || '—'}</td>
                      <td className="px-4 py-2"><StatusBadge status={claim.status} /></td>
                      <td className="px-4 py-2 font-mono text-[10px] text-muted-foreground truncate max-w-[200px]">{claim.triggerId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Workflow Config Dialog */}
      {editingWorkflow && (
        <WorkflowConfigDialog
          workflow={editingWorkflow}
          instances={instances}
          allEnvironments={Array.from(new Set([
            ...workflows.flatMap(w => w.runEnvironments || ['production']),
            ...instances.flatMap(i => i.environments || []),
          ])).sort()}
          onSaveAffinity={handleSaveAffinity}
          onClearAffinity={handleClearAffinity}
          onSaveEnvironments={handleSaveEnvironments}
          onClose={() => setEditingWorkflow(null)}
        />
      )}
    </div>
  );
};
